import React from 'react';
import { Eye, Pencil, UserCheck, UserX } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { Phone } from 'lucide-react';

/**
 * TableActions Component
 * A reusable, premium action button bar for tables
 * 
 * @param {Function} onView - Callback for view action
 * @param {Function} onEdit - Callback for edit action
 * @param {Function} onToggle - Callback for status toggle action
 * @param {boolean} isActive - Status of the row item (Active/Inactive)
 * @param {boolean} disabled - Disable actions (e.g. for admin roles)
 */
export const TableActions = ({
  onView,
  onEdit,
  onCall,
  onToggle,
  onWhatsapp,
  isActive = true,
  disabled = false,
}) => {
  return (
    <div className="flex items-center justify-start gap-2 shrink-0 py-0.5">
      {/* 1. View Detail Button */}
      {onView && (
        <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[#f0f2f5] border border-slate-200/50 shadow-[inset_1px_1px_2px_rgba(165,175,190,0.4),_inset_-1px_-1px_2px_rgba(255,255,255,0.9)] shrink-0">
          <button
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onView()}
            className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer border border-white
              ${disabled
                ? 'bg-slate-50 text-slate-350 opacity-40 cursor-not-allowed shadow-[inset_1px_1px_2px_rgba(0,0,0,0.05)]'
                : 'bg-[#f0f2f5] text-slate-500 shadow-[1.5px_1.5px_3px_rgba(165,175,190,0.4),_-1.5px_-1.5px_3px_rgba(255,255,255,0.95)] hover:text-blue-600 hover:bg-[#e6e9ef] hover:shadow-[inset_1.5px_1.5px_3px_rgba(165,175,190,0.5),_inset_-1.5px_-1.5px_3px_rgba(255,255,255,0.9)]'
              }`}
            title={disabled ? 'Disabled for protected records' : 'View Details'}
          >
            <Eye className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* 2. Edit Record Button (Orange Star state) */}
      {onEdit && (
        <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[#f0f2f5] border border-slate-200/50 shadow-[inset_1px_1px_2px_rgba(165,175,190,0.4),_inset_-1px_-1px_2px_rgba(255,255,255,0.9)] shrink-0">
          <button
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onEdit()}
            className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer border border-white
              ${disabled
                ? 'bg-slate-50 text-slate-355 opacity-40 cursor-not-allowed shadow-[inset_1px_1px_2px_rgba(0,0,0,0.05)]'
                : 'bg-[#f0f2f5] text-slate-500 shadow-[1.5px_1.5px_3px_rgba(165,175,190,0.4),_-1.5px_-1.5px_3px_rgba(255,255,255,0.95)] hover:text-[#ffa000] hover:bg-[#e6e9ef] hover:shadow-[inset_1.5px_1.5px_3px_rgba(165,175,190,0.5),_inset_-1.5px_-1.5px_3px_rgba(255,255,255,0.9)]'
              }`}
            title={disabled ? 'Disabled for protected records' : 'Edit'}
          >
            <Pencil className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* 2.5 Call Button */}
      {onCall && (
        <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[#f0f2f5] border border-slate-200/50 shadow-[inset_1px_1px_2px_rgba(165,175,190,0.4),_inset_-1px_-1px_2px_rgba(255,255,255,0.9)] shrink-0">
          <button
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onCall()}
            className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer border border-white
              ${disabled
                ? 'bg-slate-50 text-slate-400 opacity-40 cursor-not-allowed shadow-[inset_1px_1px_2px_rgba(0,0,0,0.05)]'
                : 'bg-[#f0f2f5] text-blue-500 shadow-[1.5px_1.5px_3px_rgba(165,175,190,0.4),_-1.5px_-1.5px_3px_rgba(255,255,255,0.95)] hover:text-blue-700 hover:bg-[#e6e9ef] hover:shadow-[inset_1.5px_1.5px_3px_rgba(165,175,190,0.5),_inset_-1.5px_-1.5px_3px_rgba(255,255,255,0.9)]'
              }`}
            title={disabled ? 'Disabled' : 'Call / Copy Number'}
          >
            <Phone className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* 3. Account Status Toggle Button (Green Check / Red Cross state) */}
      {onToggle && (
        isActive ? (
          <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[#f0f2f5] border border-slate-200/50 shadow-[inset_1px_1px_2px_rgba(165,175,190,0.4),_inset_-1px_-1px_2px_rgba(255,255,255,0.9)] shrink-0">
            <button
              type="button"
              onClick={onToggle}
              className="w-5 h-5 rounded-full flex items-center justify-center bg-[#f0f2f5] text-slate-500 border border-white shadow-[1.5px_1.5px_3px_rgba(165,175,190,0.4),_-1.5px_-1.5px_3px_rgba(255,255,255,0.95)] hover:text-rose-500 hover:bg-[#e6e9ef] hover:shadow-[inset_1.5px_1.5px_3px_rgba(165,175,190,0.5),_inset_-1.5px_-1.5px_3px_rgba(255,255,255,0.9)] transition-all duration-300 cursor-pointer"
              title="Deactivate Account"
            >
              <UserX className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[#f0f2f5] border border-slate-200/50 shadow-[inset_1px_1px_2px_rgba(165,175,190,0.4),_inset_-1px_-1px_2px_rgba(255,255,255,0.9)] shrink-0">
            <button
              type="button"
              onClick={onToggle}
              className="w-5 h-5 rounded-full flex items-center justify-center bg-[#f0f2f5] text-red-500 border border-white shadow-[1.5px_1.5px_3px_rgba(165,175,190,0.4),_-1.5px_-1.5px_3px_rgba(255,255,255,0.95)] hover:text-emerald-600 hover:bg-[#e6e9ef] hover:shadow-[inset_1.5px_1.5px_3px_rgba(165,175,190,0.5),_inset_-1.5px_-1.5px_3px_rgba(255,255,255,0.9)] transition-all duration-300 cursor-pointer"
              title="Activate Account"
            >
              <UserCheck className="w-3 h-3" />
            </button>
          </div>
        )
      )}

      {/* 4. WhatsApp Button */}
      {onWhatsapp && (
        <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[#f0f2f5] border border-slate-200/50 shadow-[inset_1px_1px_2px_rgba(165,175,190,0.4),_inset_-1px_-1px_2px_rgba(255,255,255,0.9)] shrink-0">
          <button
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onWhatsapp()}
            className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer border border-white
              ${disabled
                ? 'bg-slate-50 text-slate-350 opacity-40 cursor-not-allowed shadow-[inset_1px_1px_2px_rgba(0,0,0,0.05)]'
                : 'bg-[#f0f2f5] text-emerald-600 shadow-[1.5px_1.5px_3px_rgba(165,175,190,0.4),_-1.5px_-1.5px_3px_rgba(255,255,255,0.95)] hover:text-emerald-500 hover:bg-[#e6e9ef] hover:shadow-[inset_1.5px_1.5px_3px_rgba(165,175,190,0.5),_inset_-1.5px_-1.5px_3px_rgba(255,255,255,0.9)]'
              }`}
            title={disabled ? 'Disabled' : 'WhatsApp'}
          >
            <FaWhatsapp className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default TableActions;
